import { useEffect, useState, useCallback } from "react";
import api, { fileUrl, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RedactionEditor } from "@/components/RedactionEditor";
import { Loader2, ShieldOff, Image as ImageIcon, EyeOff, ScanEye, Check } from "lucide-react";

// Feature 35 — per-image privacy choice for a share link. Every supported image is explicitly
// Original / Redacted (a specific derivative) / Excluded, and the choice is persisted on the share.
const MODES = [
  { key: "original", label: "Original", Icon: ImageIcon },
  { key: "redacted", label: "Redacted", Icon: ScanEye },
  { key: "excluded", label: "Hidden", Icon: EyeOff },
];

export function ShareImagePrivacy({ propertyId, linkId }) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [selections, setSelections] = useState({});
  const [policy, setPolicy] = useState(null);
  const [derivs, setDerivs] = useState({}); // photo_id -> [derivative]
  const [editing, setEditing] = useState(null); // photo being redacted
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ps }, { data: links }] = await Promise.all([
        api.get(`/properties/${propertyId}/photos`),
        api.get(`/share-links/${propertyId}`),
      ]);
      setPhotos(ps);
      const me = (links || []).find((l) => l.link_id === linkId);
      setSelections((me && me.image_selections) || {});
      setPolicy((me && me.selection_policy) || "legacy");
    } catch { toast.error("Couldn't load photos."); } finally { setLoading(false); }
  }, [propertyId, linkId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const loadDerivs = async (photoId) => {
    try { const { data } = await api.get(`/photos/${photoId}/redactions`); setDerivs((d) => ({ ...d, [photoId]: data })); }
    catch { /* ignore */ }
  };

  const setMode = async (photoId, mode, derivativeId) => {
    try {
      const { data } = await api.put(`/share-links/${linkId}/image-selection`,
        { photo_id: photoId, mode, derivative_id: derivativeId || null });
      setSelections(data.image_selections || {});
      toast.success(mode === "excluded" ? "Image hidden from this share." : mode === "redacted" ? "Redacted copy selected." : "Original selected.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update this image."); }
  };

  const onRedactClick = async (photoId) => { await loadDerivs(photoId); setEditing(photoId); };

  return (
    <div className="mt-3 border-t border-[#F1F5F9] pt-3" data-testid={`share-privacy-${linkId}`}>
      <button data-testid={`share-privacy-toggle-${linkId}`} onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#475569]">
        <ShieldOff size={13} className="text-[#7C3AED]" /> Privacy: choose what each photo shows {open ? "▾" : "▸"}
      </button>
      {open && (
        <div className="mt-2" data-testid={`share-privacy-panel-${linkId}`}>
          {loading ? <div className="py-4 flex justify-center"><Loader2 className="animate-spin text-[#94A3B8]" size={16} /></div> : (
            <div className="space-y-2">
              {photos.length === 0 && <p className="text-xs text-[#94A3B8]">No shareable photos yet.</p>}
              {policy === "explicit" && (() => {
                const unset = photos.filter((p) => !selections[p.photo_id]?.mode).length;
                return unset > 0 ? (
                  <p data-testid={`share-unselected-note-${linkId}`} className="text-[11px] text-[#B45309] bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                    {unset} of {photos.length} image{photos.length === 1 ? "" : "s"} not selected — they stay private until you choose Original, Redacted, or Hidden.
                  </p>
                ) : (
                  <p data-testid={`share-allset-note-${linkId}`} className="text-[11px] text-emerald-700">Every image has an explicit choice.</p>
                );
              })()}
              {photos.map((p) => {
                const sel = selections[p.photo_id];
                const chosen = sel?.mode;
                const unselected = policy === "explicit" && !chosen;
                return (
                  <div key={p.photo_id} data-testid={`privacy-row-${p.photo_id}`} className={`flex items-center gap-2 rounded-lg border p-2 ${unselected ? "border-amber-300 bg-amber-50/40" : "border-[#E2E8F0]"}`}>
                    <img src={fileUrl(p.storage_path, 120)} alt="" className="h-11 w-11 rounded object-cover bg-[#F1F5F9] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-[#475569] truncate">{p.room_name || "Photo"}{unselected && <span className="ml-1 text-[#B45309] font-medium">· Not selected</span>}</div>
                      <div className="mt-1 flex gap-1">
                        {MODES.map((m) => {
                          const active = chosen === m.key;
                          const onClick = m.key === "redacted" ? () => onRedactClick(p.photo_id) : () => setMode(p.photo_id, m.key);
                          return (
                            <button key={m.key} data-testid={`privacy-${m.key}-${p.photo_id}`} onClick={onClick}
                              className={`inline-flex items-center gap-1 rounded px-1.5 py-1 text-[10px] font-medium border ${active ? "bg-navy text-white border-navy" : "bg-white text-[#475569] border-[#E2E8F0]"}`}>
                              <m.Icon size={10} /> {m.label}{active && m.key !== "redacted" && <Check size={9} />}
                            </button>
                          );
                        })}
                      </div>
                      {chosen === "redacted" && <div className="mt-0.5 text-[10px] text-[#7C3AED]">Redacted copy selected</div>}
                      {editing === p.photo_id && (derivs[p.photo_id] || []).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`derivative-picker-${p.photo_id}`}>
                          {(derivs[p.photo_id] || []).map((d) => (
                            <button key={d.derivative_id} data-testid={`pick-derivative-${d.derivative_id}`}
                              onClick={() => { setMode(p.photo_id, "redacted", d.derivative_id); setEditing(null); }}
                              className={`rounded border overflow-hidden ${sel?.derivative_id === d.derivative_id ? "border-navy" : "border-[#E2E8F0]"}`}>
                              <img src={`${API}${d.file_url}`} alt="" className="h-11 w-11 object-cover bg-black" />
                            </button>
                          ))}
                          <Button data-testid={`draw-new-redaction-${p.photo_id}`} size="sm" variant="outline" className="h-11 rounded text-[10px]" onClick={() => setEditing("editor:" + p.photo_id)}>Draw new</Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {editing && (editing.startsWith("editor:") || !(derivs[editing] || []).length) && (
        <RedactionEditor
          photoId={editing.replace("editor:", "")}
          storagePath={(photos.find((p) => p.photo_id === editing.replace("editor:", "")) || {}).storage_path}
          onClose={() => setEditing(null)}
          onSaved={(d) => setMode(d.original_photo_id, "redacted", d.derivative_id)}
        />
      )}
    </div>
  );
}
